import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { Input } from '@goaltree/ui/input'
import { cn } from '@goaltree/ui/lib/utils'
import { useAction } from 'convex/react'
import { Loader2, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

export function GoalQuickAdd({ goalId, className }: { goalId: Id<'nodes'>; className?: string }) {
  const { t } = useTranslation()
  const addTask = useAction(api.goalAddTaskNode.addTask)
  const [title, setTitle] = useState('')
  const [pending, setPending] = useState<{ id: number; text: string }[]>([])

  const submit = () => {
    const text = title.trim()
    if (!text) return
    setTitle('')
    const id = Date.now() + Math.random()
    setPending((p) => [...p, { id, text }])
    addTask({ goalId, title: text })
      .then((r) =>
        toast.success(t('goals.addedTo', { milestone: r.milestoneTitle, target: r.targetTitle })),
      )
      .catch(() => {
        setTitle((cur) => cur || text)
        toast.error(t('goals.addTaskFailed'))
      })

      .finally(() => setPending((p) => p.filter((x) => x.id !== id)))
  }

  return (
    <div className={className}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        className="flex items-center gap-2 rounded-lg border border-border/60 bg-card px-3 py-1.5 focus-within:border-primary/50"
      >
        <Plus className="size-4 shrink-0 text-muted-foreground" strokeWidth={2} />
        <Input
          aria-label={t('goals.addTask')}
          placeholder={t('goals.addTaskAi')}
          value={title}
          onChangeText={setTitle}
          className="h-7 border-0 bg-transparent px-0 text-sm shadow-none outline-none focus-visible:border-0 focus-visible:ring-0 dark:bg-transparent md:text-sm"
        />
        <button
          type="submit"
          disabled={!title.trim()}
          className={cn(
            'shrink-0 cursor-pointer rounded-md px-2.5 py-1 text-xs font-medium text-primary-text transition-colors hover:bg-muted disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent',
          )}
        >
          {t('goals.addTask')}
        </button>
      </form>
      {pending.map((p) => (
        <div
          key={p.id}
          className="flex items-center gap-2 px-3 pt-1.5 text-xs text-muted-foreground"
        >
          <Loader2 className="size-3 shrink-0 animate-spin" />
          <span className="truncate">
            {t('goals.addingTask')} {p.text}
          </span>
        </div>
      ))}
    </div>
  )
}
